class FaberOosNotify extends HTMLElement {
  connectedCallback() {
    this.isItalian = document.documentElement.lang?.toLowerCase().startsWith('it') || false;
    this.variants = this.readVariants();
    this.optionNames = this.readJson('[data-oos-option-names]');
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    this.onGroupedAvailability = this.onGroupedAvailability.bind(this);

    document.addEventListener('variant:update', this.onVariantUpdate);
    document.addEventListener('grouped:availability-change', this.onGroupedAvailability);

    const initialVariant = this.variants.find(
      (item) => String(item.id) === String(this.dataset.currentVariantId || '')
    );
    if (initialVariant) this.update(initialVariant);
    // Shopify customer forms can share a success state across newsletter and OOS forms.
    // Only the OOS form that was actually submitted may display this confirmation.
    this.setupSubmissionTracking();
    this.showSuccessPopup();
  }

  setupSubmissionTracking() {
    const form = this.querySelector('.faber-oos-notify__form');
    if (!form) return;
    // Shopify may change the URL or section/block IDs after a customer-form POST.
    // A stable, one-use marker avoids relying on either of them.
    this.submissionKey = 'faber-oos-pending-submission';
    if (!FaberOosNotify.submissionListenerInstalled) {
      FaberOosNotify.submissionListenerInstalled = true;
      document.addEventListener('submit', (event) => {
        const submittedForm = event.target;
        if (!(submittedForm instanceof HTMLFormElement)) return;
        if (!submittedForm.matches('form[action*="/contact"]')) return;
        try {
          if (submittedForm.matches('.faber-oos-notify__form')) {
            sessionStorage.setItem('faber-oos-pending-submission', '1');
          } else {
            sessionStorage.removeItem('faber-oos-pending-submission');
          }
        } catch (_) { /* Storage may be unavailable. */ }
      }, true);
    }
  }

  showSuccessPopup() {
    const success = this.querySelector('.faber-oos-notify__message--success');
    if (!success || success.dataset.popupShown === 'true') return;
    let submittedHere = false;
    try {
      submittedHere = sessionStorage.getItem(this.submissionKey) === '1';
      if (submittedHere) sessionStorage.removeItem(this.submissionKey);
    } catch (_) { /* Storage may be unavailable. */ }
    if (!submittedHere) {
      success.hidden = true;
      return;
    }
    if (document.getElementById('faber-oos-success-dialog')) return;

    const dialog = document.createElement('dialog');
    if (typeof dialog.showModal !== 'function') return;
    success.dataset.popupShown = 'true';
    dialog.id = 'faber-oos-success-dialog';
    dialog.setAttribute('aria-labelledby', 'faber-oos-success-title');
    dialog.setAttribute('aria-describedby', 'faber-oos-success-description');

    const style = document.createElement('style');
    style.textContent = `
      #faber-oos-success-dialog:not([open]) { display: none; }
      #faber-oos-success-dialog[open] {
        display: block; position: fixed; inset: 0; margin: auto;
        box-sizing: border-box; width: min(440px, calc(100vw - 32px));
        max-height: calc(100dvh - 48px); overflow: auto;
        padding: 32px 28px; border: 1px solid #e4e4e4; border-radius: 16px;
        background: #fff; color: #191919; text-align: center;
        font-family: inherit; box-shadow: 0 24px 80px #0003;
      }
      #faber-oos-success-dialog::backdrop { background: rgb(0 0 0 / 40%); }
      #faber-oos-success-dialog h2 { margin: 0 0 12px; font-size: 22px; line-height: 1.25; }
      #faber-oos-success-dialog p { margin: 0 0 24px; font-size: 15px; line-height: 1.55; }
      #faber-oos-success-dialog button {
        font: inherit; font-weight: 600; background: #191919; color: #fff;
        border: 0; border-radius: 8px; padding: 13px 24px; min-height: 46px; cursor: pointer;
      }
      #faber-oos-success-dialog button:focus-visible { outline: 2px solid #191919; outline-offset: 4px; }
    `;
    const title = document.createElement('h2');
    title.id = 'faber-oos-success-title';
    title.textContent = this.isItalian ? 'Richiesta ricevuta!' : 'Request received!';
    const description = document.createElement('p');
    description.id = 'faber-oos-success-description';
    description.textContent = this.isItalian
      ? 'Ti avviseremo via email quando la configurazione scelta sarà nuovamente disponibile.'
      : 'We’ll email you when your chosen configuration is available again.';
    const button = document.createElement('button');
    button.type = 'button';
    button.autofocus = true;
    button.textContent = this.isItalian ? 'Ho capito' : 'Got it';
    button.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => dialog.remove(), { once: true });
    dialog.append(style, title, description, button);
    document.body.append(dialog);
    dialog.showModal();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.onVariantUpdate);
    document.removeEventListener('grouped:availability-change', this.onGroupedAvailability);
  }

  readVariants() {
    return this.readJson('[data-oos-variants]');
  }

  readJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || '[]');
    } catch (error) {
      console.warn(`Faber OOS: unable to read ${selector}`, error);
      return [];
    }
  }

  onVariantUpdate(event) {
    const detail = event.detail || {};
    const productId = String(detail.data?.newProduct?.id || detail.data?.productId || '');
    if (productId && productId !== String(this.dataset.productId)) return;

    const resource = detail.resource;
    const variant = resource?.id
      ? this.variants.find((item) => String(item.id) === String(resource.id)) || resource
      : resource;

    const unavailableComponents = this.readUnavailableComponents(detail.data?.html);
    this.update(variant, unavailableComponents);
  }

  onGroupedAvailability(event) {
    const entry = event.detail;
    if (!entry?.variantId) return;

    this.dataset.productHandle = entry.handle || this.dataset.productHandle || '';
    this.dataset.currentVariantId = String(entry.variantId);

    this.update(
      {
        id: entry.variantId,
        available: !!entry.variantAvailable,
        title: entry.handle || '',
        options: [],
      },
      Array.isArray(entry.unavailableComponents) ? entry.unavailableComponents : []
    );
  }

  readUnavailableComponents(html) {
    if (!html?.querySelectorAll) return [];

    return Array.from(
      html.querySelectorAll('.variant-picker input:checked[data-option-available="false"]')
    ).map((input) => {
      const fieldset = input.closest('fieldset');
      const optionName = fieldset?.querySelector('legend')?.textContent?.trim();
      const optionValue = input.value || input.getAttribute('aria-label') || '';
      return optionName ? `${optionName} — ${optionValue}` : optionValue;
    }).filter(Boolean);
  }

  update(variant, unavailableComponents = []) {
    const unavailable = !variant || variant.available === false;

    this.hidden = !unavailable;
    this.classList.toggle('faber-oos-notify--hidden', !unavailable);

    if (!unavailable || !variant?.id) return;

    const baseTag = this.dataset.baseTag || 'Out of stock: macchina';
    const productHandle = this.dataset.productHandle || '';
    const tags = [baseTag, `OOS product: ${productHandle}`, `OOS variant: ${variant.id}`].join(', ');
    const tagInput = this.querySelector('[data-oos-tags]');
    if (tagInput) tagInput.value = tags;

    this.updateDetail(variant, unavailableComponents);
  }

  updateDetail(variant, unavailableComponents = []) {
    const detail = this.querySelector('[data-oos-detail]');
    if (!detail || !variant) return;

    if (!unavailableComponents.length) {
      unavailableComponents = (variant.options || []).reduce((components, optionValue, optionIndex) => {
        const optionExistsInStock = this.variants.some(
          (candidate) => candidate.available && candidate.options?.[optionIndex] === optionValue
        );

        if (!optionExistsInStock) {
          const optionName = this.optionNames?.[optionIndex];
          components.push(optionName ? `${optionName} — ${optionValue}` : optionValue);
        }

        return components;
      }, []);
    }

    const variantOptions = Array.isArray(variant.options) && variant.options.length
      ? variant.options
      : [variant.title].filter(Boolean);
    const values = unavailableComponents.length ? unavailableComponents : variantOptions;
    const label = values.join(' / ');

    if (unavailableComponents.length) {
      detail.innerHTML = this.isItalian
        ? `Componente non disponibile: <strong>${this.escapeHtml(label)}</strong>.`
        : `Unavailable component: <strong>${this.escapeHtml(label)}</strong>.`;
    } else {
      detail.innerHTML = this.isItalian
        ? `Configurazione non disponibile: <strong>${this.escapeHtml(label)}</strong>.`
        : `Unavailable configuration: <strong>${this.escapeHtml(label)}</strong>.`;
    }
  }

  escapeHtml(value) {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }
}

if (!customElements.get('faber-oos-notify')) {
  customElements.define('faber-oos-notify', FaberOosNotify);
}
